import { useLogin } from '@refinedev/core'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { saveLoginRedirectTo } from '../../lib/login-redirect'
import { signInWithApple } from '../../lib/social-auth/apple-auth'
import { signInWithKakao } from '../../lib/social-auth/kakao-auth'
import { signInWithNaver } from '../../lib/social-auth/naver-auth'
import { logger } from '../../lib/logger'
import { useGoogleSignInButton } from './useGoogleSignInButton'

type SocialProvider = 'GOOGLE' | 'APPLE' | 'KAKAO' | 'NAVER'

export function useLoginPage() {
  const { t } = useTranslation()
  const { mutate: login, isPending } = useLogin()
  const [error, setError] = useState<string | null>(null)
  const [loadingProvider, setLoadingProvider] = useState<SocialProvider | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)

  useEffect(() => {
    const expired = localStorage.getItem('session_expired')
    if (expired === 'true') {
      setSessionExpired(true)
      localStorage.removeItem('session_expired')
    }
  }, [])

  // Remember where the session expired once, on mount: saving it only in the Kakao handler
  // meant Google and Apple logins always landed on the dashboard instead of coming back here.
  useEffect(() => {
    const toParam = new URLSearchParams(window.location.search).get('to')
    if (toParam) saveLoginRedirectTo(decodeURIComponent(toParam))
  }, [])

  const handleSocialLogin = useCallback(
    async (provider: SocialProvider, getToken: () => Promise<string>) => {
      setError(null)
      setLoadingProvider(provider)
      try {
        const idToken = await getToken()
        login(
          { provider, idToken },
          {
            onError: (err) => {
              setError(err.message ?? t('common.errors.loginFailedMessage'))
            },
          },
        )
      } catch (error_) {
        logger.error(`[Login] ${provider} auth error:`, error_)
        setError(error_ instanceof Error ? error_.message : t('common.errors.loginFailedMessage'))
      } finally {
        setLoadingProvider(null)
      }
    },
    [login, t],
  )

  const handleGoogleCredential = useCallback(
    (credential: string) => {
      setError(null)
      setLoadingProvider('GOOGLE')
      login(
        { provider: 'GOOGLE', idToken: credential },
        {
          onError: (err) => {
            setError(err.message ?? t('common.errors.loginFailedMessage'))
            setLoadingProvider(null)
          },
          onSuccess: () => {
            setLoadingProvider(null)
          },
        },
      )
    },
    [login, t],
  )

  const { containerRef: googleButtonRef, error: googleButtonError } =
    useGoogleSignInButton(handleGoogleCredential)

  useEffect(() => {
    if (googleButtonError) {
      setError(googleButtonError)
    }
  }, [googleButtonError])

  const handleKakaoLogin = useCallback(() => {
    setError(null)
    setLoadingProvider('KAKAO')
    signInWithKakao().catch((error_: unknown) => {
      logger.error('[Login] Kakao auth error:', error_)
      setError(error_ instanceof Error ? error_.message : t('common.errors.loginFailedMessage'))
      setLoadingProvider(null)
    })
  }, [t])

  const handleNaverLogin = useCallback(() => {
    setError(null)
    setLoadingProvider('NAVER')
    try {
      signInWithNaver()
    } catch (error_) {
      logger.error('[Login] Naver auth error:', error_)
      setError(error_ instanceof Error ? error_.message : t('common.errors.loginFailedMessage'))
      setLoadingProvider(null)
    }
  }, [t])

  const handleAppleLogin = useCallback(() => {
    void handleSocialLogin('APPLE', signInWithApple)
  }, [handleSocialLogin])

  return {
    t,
    error,
    sessionExpired,
    isPending: isPending || loadingProvider !== null,
    loadingProvider,
    googleButtonRef,
    handleKakaoLogin,
    handleNaverLogin,
    handleAppleLogin,
  }
}
